import "molstar/lib/mol-plugin-ui/skin/light.scss";

import React, {createRef, useEffect, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {Col, Divider, Input, Modal, Row, Table, Tabs, Tooltip, Typography} from "antd";


import {createPluginUI} from "molstar/lib/mol-plugin-ui";
import {renderReact18} from "molstar/lib/mol-plugin-ui/react18";
import InteractionInnerTable from "./interactionInnerTable"

import {MolScriptBuilder as MS, MolScriptBuilder} from 'molstar/lib/mol-script/language/builder';
import {setStructureOverpaint} from 'molstar/lib/mol-plugin-state/helpers/structure-overpaint'
import {StructureSelection} from 'molstar/lib/mol-model/structure/query';
import {Script} from 'molstar/lib/mol-script/script';
import {PluginConfig} from 'molstar/lib/mol-plugin/config';
import {DefaultPluginUISpec} from 'molstar/lib/mol-plugin-ui/spec';
import {StateSelection, StateTransform} from 'molstar/lib/mol-state';
import {StructureElement, StructureProperties as Props} from 'molstar/lib/mol-model/structure'
import {OrderedSet} from "molstar/lib/mol-data/int.js";
import {bundleLabel} from "molstar/lib/mol-theme/label.js"

import {Color} from "molstar/lib/mol-util/color";

import {addCustomInteraction} from "../contact-drawer/customInteraction.js";
import CompoundTable from "./compoundTable.js"

import Multichart from "./multichart.js"
import {RING_ATOMS} from "../data/ring.js"
import {INTERACTION_COLOR} from "../utils/colorMap.js"
import {CONSTANTS} from "../utils/constant";
import {InfoCircleOutlined} from "@ant-design/icons";

const {Text} = Typography;

export default function MoleStar({setMenuIsVisible, setResultTitle, setResultPath}) {

    const [mainData, setMainData] = useState(null);
    const [sortedData, setSortedData] = useState(null);

    const [clickedInteraction, setClickedInteraction] = useState(null);
    const [firstRowDivHeight, setFirstRowDivHeight] = useState(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [results, setResults] = useState(null);

    const [searchTextRes2, setSearchTextRes2] = useState("")

    const [noResultModal, setNoResultModal] = useState(false);

    const parent = createRef();
    const container = useRef(null);

    const [sortOrderState, setSortOrderState] = useState({col: ""});

    const [mainTableColumns, setMainTableColumns] = useState([]);

    const csvToData = (csv) => {
        if (!csv)
            return;

        let data = [];
        let rows = csv.split("\n");
        rows.shift();
        rows.forEach(row => {
            if (row) {
                data.push(row.split(","));
            }
        });
        return data;
    }

    const updateHeight = () => {
        if (container.current) {
            setFirstRowDivHeight(container.current.offsetHeight - 5);
        }
    };

    useEffect(() => {
        updateHeight();

        // Aggiunge l'evento di resize
        window.addEventListener("resize", updateHeight);

        // Pulizia dell'evento al momento dello smontaggio
        return () => {
            window.removeEventListener("resize", updateHeight);
        };

    }, []);

    const getLoci = (item) => {
        const {structure, chainId, resId, atomName} = item;

        let selection;

        if (chainId && resId && atomName) {
            selection = Script.getStructureSelection(
                (Q) =>
                    Q.struct.generator.atomGroups({
                        'chain-test': Q.core.rel.eq([Q.struct.atomProperty.macromolecular.auth_asym_id(), chainId]),
                        'residue-test': Q.core.rel.eq([Q.struct.atomProperty.macromolecular.auth_seq_id(), resId]),
                        'atom-test': Q.core.rel.eq([Q.atomName(atomName), Q.ammp('label_atom_id')])
                    }),
                structure
            );
        }

        if (chainId && resId && Array.isArray(atomName)) {
            const Q = MolScriptBuilder;
            const groups = [];
            for (let an of atomName) {
                groups.push(Q.struct.generator.atomGroups({
                    'chain-test': Q.core.rel.eq([Q.struct.atomProperty.macromolecular.auth_asym_id(), chainId]),
                    'residue-test': Q.core.rel.eq([Q.struct.atomProperty.macromolecular.auth_seq_id(), resId]),
                    'atom-test': Q.core.rel.eq([Q.atomName(an), Q.ammp('label_atom_id')])
                }));
            }
            selection = Script.getStructureSelection(
                (Q) => Q.struct.combinator.merge(groups), structure);
        }

        return StructureSelection.toLociWithSourceUnits(selection);
    };

    // add interaction contact
    const addContact = async (contactPair, tag) => {
        const {pair, interactionType, color} = contactPair ?? {};
        if (pair.length !== 2) {
            return;
        }
        const loci1 = getLoci(pair[0]);
        const loci2 = getLoci(pair[1]);
        if (loci1 && loci2) {
            await addCustomInteraction(loci1, loci2, {color: color, tags: tag, reprTags: tag, selectionTags: tag});
        }
    };

    const renderInteractions = (text, record) => {
        let string = '';

        const occurrences = text.split(';').reduce((acc, item) => {
            if (item.trim()) {
                acc[item] = (acc[item] || 0) + 1;
            }
            return acc;
        }, {});

        for (const [key, value] of Object.entries(occurrences)) {
            let occ = value > 1 ? ` (${value}),` : ',';
            //let interactionName = key.includes('*') ? `<span style="color:red">${key}</span>` : key;
            string += ` ${key}${occ}`;
        }

        return <div dangerouslySetInnerHTML={{__html: string}}></div>;
    }
    const handleSearchRes2 = (value) => {
        setSearchTextRes2(value.toLowerCase().replace('pi', 'π'));
    };


    const zoom_in = (args) => {
        const objdata = window.molstar.managers.structure.hierarchy.current.structures[0].cell.obj.data;

        window.molstar.canvas3d.setProps({
            renderer: {}, marking: {
                highlightEdgeColor: Color.fromRgb(255, 255, 0),
                selectEdgeColor: Color.fromRgb(255, 255, 0)
            }
        });

        const sel = Script.getStructureSelection(Q => Q.struct.combinator.merge([
            Q.struct.generator.atomGroups({
                "chain-test": MS.core.rel.eq(
                    [MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(),
                        args[0][0]]),
                'residue-test': Q.core.set.has(
                    [Q.set(parseInt(args[0][1])), Q.ammp('auth_seq_id')])
            }),
            Q.struct.generator.atomGroups({
                "chain-test": MS.core.rel.eq(
                    [MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(),
                        args[1][0]]),
                'residue-test': Q.core.set.has(
                    [Q.set(parseInt(args[1][1])), Q.ammp('auth_seq_id')])
            }),
        ]), objdata);

        let loci = StructureSelection.toLociWithSourceUnits(sel);
        window.molstar.managers.camera.focusLoci(loci);

        window.molstar.managers.interactivity.lociSelects.deselectAll();
        window.molstar.managers.interactivity.lociSelects.select({loci: loci});
    }

    const selectInMol = async (args, color, interactiontype, nested = false, zoomin = true, tag = "") => {
        const objdata = window.molstar.managers.structure.hierarchy.current.structures[0].cell.obj.data;

        if (zoomin) {
            zoom_in(args);
        }

        //window.molstar.managers.interactivity.lociHighlights.highlightOnly({ loci });

        if (nested) {
            await addContact({
                pair: [{
                    structure: objdata,
                    chainId: args[0][0],
                    resId: parseInt(args[0][1]),
                    atomName: args[0][2]
                }, {
                    structure: objdata,
                    chainId: args[1][0],
                    resId: parseInt(args[1][1]),
                    atomName: args[1][2]
                }],
                interactionType: interactiontype,
                color: color
            }, tag);
        }

    }

    const getPdb = async (folder, pdb) => {

        const postData = new FormData();
        postData.append("folder", folder);
        postData.append("pdb", pdb);

        try {
            const response = await fetch(
                CONSTANTS.PHP_SCRIPT_BASE_PATH + 'get_pdb.php', {
                    method: 'POST',
                    body: postData
                });

            const result = await response.json();
            return result.data;

        } catch (error) {
            console.error('Errore durante la richiesta:', error);
        }
    };

    const getMainTable = async (folder, pdb, table, chain1, chain2) => {

        const postData = new FormData();
        postData.append("folder", folder);
        postData.append("pdb", pdb);
        postData.append("tableName", table);
        postData.append("chain1", chain1);
        postData.append("chain2", chain2);

        try {
            const response = await fetch(
                CONSTANTS.PHP_SCRIPT_BASE_PATH + 'get_table.php', {
                    method: 'POST',
                    body: postData
                });

            const result = await response.json();
            return result.data;

        } catch (error) {
            console.error('Errore durante la richiesta:', error);
        }
    };

    const sortInteractionTypeRowOnClick = (interaction_type) => {
        setSortedData((data) => {
            const newSortedData = [...data].sort((a, b) => {
                const isA = a[7].includes(interaction_type);
                const isB = b[7].includes(interaction_type);

                if (isA && !isB) {
                    a[8] = true
                    return -1; // a viene prima di b
                }

                if (!isA && isB) {
                    b[8] = true
                    return 1; // b viene prima di a
                }

                if (!isA && !isB) {
                    a[8] = false
                    b[8] = false
                }

            });
            return newSortedData
        });
    }

    const sortSingleRowOnClick = (auth_seq_id1, auth_asym_id1, comp_id1, auth_seq_id2, auth_asym_id2, comp_id2) => {
        setSortedData((data) => {
            const newSortedData = [...data].sort((a, b) => {
                const isA = (auth_asym_id1 == a[3] && auth_seq_id1 == a[2] && comp_id1 == a[1]) && (auth_asym_id2 == a[6] && auth_seq_id2 == a[5] && comp_id2 == a[4]);
                const isB = (auth_asym_id1 == b[3] && auth_seq_id1 == b[2] && comp_id1 == b[1]) && (auth_asym_id2 == b[6] && auth_seq_id2 == b[5] && comp_id2 == b[4]);

                if (isA && !isB) {
                    a[8] = true
                    return -1; // a viene prima di b
                }

                if (!isA && isB) {
                    b[8] = true
                    return 1; // b viene prima di a
                }

                if (!isA && !isB) {
                    a[8] = false
                    b[8] = false
                }

            });
            return newSortedData
        });
    }

    const sortOnClick = (auth_seq_id, auth_asym_id, comp_id) => {
        setSortedData((data) => {
            const newSortedData = [...data].sort((a, b) => {
                const isA = (auth_asym_id == a[3] && auth_seq_id == a[2] && comp_id == a[1]) || (auth_asym_id == a[6] && auth_seq_id == a[5] && comp_id == a[4]);
                const isB = (auth_asym_id == b[3] && auth_seq_id == b[2] && comp_id == b[1]) || (auth_asym_id == b[6] && auth_seq_id == b[5] && comp_id == b[4]);

                if (isA && isB) {
                    a[8] = true
                    b[8] = true
                    return 0;
                }

                if (isA && !isB) {
                    a[8] = true
                    return -1; // a viene prima di b
                }

                if (!isA && isB) {
                    b[8] = true
                    return 1; // b viene prima di a
                }

                if (!isA && !isB) {
                    a[8] = false
                    b[8] = false
                }

            });
            return newSortedData
        });
    }

    useEffect(() => {

        const res1Name = (results?.mol1 || 'Res 1');
        const res2Name = (results?.mol2 || 'Res 2');

        //,Res. Name 1,Res. Number 1,Chain 1,Res. Name 2,Res. Number 2,Chain 2,Type of Interactions
        setMainTableColumns(
            [
                {title: res1Name, key: 0, render: (row) => row[1] + row[2] + "_" + row[3], sorter: (a, b) => a[2] - b[2], sortOrder: sortOrderState.col === res1Name ? sortOrderState.order : null},
                {title: res2Name, key: 1, render: (row) => row[4] + row[5] + "_" + row[6], sorter: (a, b) => a[5] - b[5], sortOrder: sortOrderState.col === res2Name ? sortOrderState.order : null},
                {
                    title: 'Interactions', dataIndex: 7, key: 6, render: renderInteractions, filterDropdown: () => (
                        <div style={{padding: 8}}>
                            <Input
                                placeholder="Filter by interaction name"
                                value={searchTextRes2}
                                onChange={(e) => handleSearchRes2(e.target.value)}
                                style={{marginBottom: 8, display: "block"}}
                            />
                        </div>
                    ),
                    onFilter: (value, record) =>
                        record[7].toLowerCase().includes(searchTextRes2),
                    filteredValue: [searchTextRes2], // Passa il valore corrente del filtro
                }
            ]);
    }, [results, searchTextRes2, sortOrderState]);

    // In debug mode of react's strict mode, this code will
    // be called twice in a row, which might result in unexpected behavior.
    useEffect(() => {
        async function init() {

            try {
                setMenuIsVisible(false);

                let dataFolder = "";
                let pdb = "";
                let chain1 = "";
                let chain2 = "";
                let projectName = "";
                let mol1 = "";
                let mol2 = "";


                if (searchParams.get("f") && searchParams.get("p") && searchParams.get("c1") && searchParams.get("c2")) {
                    dataFolder = atob(searchParams.get("f"));
                    pdb = atob(searchParams.get("p"));
                    chain1 = atob(searchParams.get("c1"));
                    chain2 = atob(searchParams.get("c2"));
                    projectName = atob(searchParams.has("pn") ? searchParams.get("pn") : "");
                    mol1 = atob(searchParams.has("mol1") ? searchParams.get("mol1") : "");
                    mol2 = atob(searchParams.has("mol2") ? searchParams.get("mol2") : "");
                    setResults({
                        dataFolder: dataFolder,
                        pdb: pdb,
                        chain1: chain1,
                        chain2: chain2,
                        projectName: projectName,
                        mol1: mol1,
                        mol2: mol2
                    });
                    setResultTitle("Results for " + pdb + " chain " + chain1 + " vs " + chain2);
                    setResultPath(dataFolder);

                } else {
                    navigate("/");
                }

                //D:\UNISA\CHIMICA\progetti_cm_app\consrank_2.0\node_modules\molstar\lib\mol-plugin\spec.js
                //D:\UNISA\CHIMICA\progetti_cm_app\consrank_2.0\node_modules\molstar\lib\mol-canvas3d\canvas3d.d.ts
                const defaultPluginSpec = DefaultPluginUISpec();
                const myBehavior = [];
                myBehavior.push(defaultPluginSpec.behaviors[0]);
                myBehavior.push(defaultPluginSpec.behaviors[1]);
                myBehavior.push(defaultPluginSpec.behaviors[2]);
                myBehavior.push(defaultPluginSpec.behaviors[5]);
                myBehavior.push(defaultPluginSpec.behaviors[6]);
                myBehavior.push(defaultPluginSpec.behaviors[7]);
                myBehavior.push(defaultPluginSpec.behaviors[8]);
                myBehavior.push(defaultPluginSpec.behaviors[9]);
                myBehavior.push(defaultPluginSpec.behaviors[10]);
                myBehavior.push(defaultPluginSpec.behaviors[11]);
                myBehavior.push(defaultPluginSpec.behaviors[12]);
                myBehavior.push(defaultPluginSpec.behaviors[13]);
                myBehavior.push(defaultPluginSpec.behaviors[14]);

                const MySpec = {
                    ...defaultPluginSpec,
                    canvas3d: {
                        renderer: {
                            backgroundColor: Color.fromRgb(255, 255, 255)
                        },
                        //cameraClipping: { far: true, minNear: 5 }
                    },
                    layout: {
                        initial: {
                            isExpanded: false,
                            showControls: false,
                            controlsDisplay: "reactive",
                        },
                    },
                    /*
                    components: {
                        remoteState: "none",
                        controls: { left: "none", bottom: "none", right: "none" },
                    },*/
                    config: [
                        [PluginConfig.Viewport.ShowExpand, true],
                        [PluginConfig.Viewport.ShowControls, true],
                        [PluginConfig.Viewport.ShowSettings, true],
                        [PluginConfig.Viewport.ShowSelectionMode, true],
                        [PluginConfig.Viewport.ShowTrajectoryControls, true],
                        [PluginConfig.Viewport.ShowAnimation, true],
                        [PluginConfig.Viewport.ShowTrajectoryControls, true],
                    ],
                    behaviors: myBehavior
                };

                window.molstar = await createPluginUI({
                    target: parent.current,
                    render: renderReact18,
                    spec: MySpec
                });

                const PDB_FILE = await getPdb(dataFolder, `${pdb}.pd_h.pdb_${chain1}_${chain2}_truncated.pdb`);
                const MAIN_TALBE = await getMainTable(dataFolder, pdb, 'final_file', chain1, chain2);
                const MAIN_TABLE_DATA = csvToData(MAIN_TALBE);

                setMainData(MAIN_TABLE_DATA);
                setSortedData(MAIN_TABLE_DATA);

                if (!MAIN_TALBE) {
                    setNoResultModal(true);
                }

                const data = await window.molstar.builders.data.rawData({
                    data: PDB_FILE, /* PDB */
                    label: void 0 /* optional label */
                });

                const trajectory = await window.molstar.builders.structure.parseTrajectory(data, "pdb");
                const model = await window.molstar.builders.structure.createModel(trajectory);
                const structure = await window.molstar.builders.structure.createStructure(model);

                const components = {
                    polymer: await window.molstar.builders.structure.tryCreateComponentStatic(structure, 'polymer'),
                    water: await window.molstar.builders.structure.tryCreateComponentStatic(structure, 'water'),
                    ion: await window.molstar.builders.structure.tryCreateComponentStatic(structure, "ion"),
                    //ligand: await window.molstar.builders.structure.tryCreateComponentStatic(structure, 'ligand'), (SERVE ???)
                };

                const builder = window.molstar.builders.structure.representation;
                const update = window.molstar.build();

                if (components.polymer) {
                    builder.buildRepresentation(update, components.polymer, {type: 'cartoon', typeParams: {alpha: 0.6}}, {tag: 'polymer'});
                }
                if (components.water) {
                    builder.buildRepresentation(update, components.water, {type: 'ball-and-stick', typeParams: {alpha: 1.0}}, {tag: 'water'});
                }
                if (components.ion) {
                    builder.buildRepresentation(update, components.ion, {type: 'ball-and-stick', typeParams: {alpha: 0.6}}, {tag: 'ion'});
                }
                //if (components.ligand) builder.buildRepresentation(update, components.ligand, { type: 'ball-and-stick' }, { tag: 'ligand' });

                await update.commit();

                await selectAllRes(MAIN_TABLE_DATA);

                // Vedi mol-theme/label.js
                window.molstar.behaviors.interaction.click.subscribe(
                    (e) => {

                        if (e.current.loci.kind === "group-loci") {
                            selectedInteraction(e.current.repr.label);
                            let loci = e.current.loci;
                            const g = loci.groups[0];
                            console.log(g);
                            const os = OrderedSet.start(g.ids);
                            console.log(os);
                            let c = g ? loci.shape.getLabel(os, g.instance) : '';
                            console.log(c)
                        }

                        if (e.current.loci.kind === 'bond-loci') {
                            const bond = e.current.loci.bonds[0];
                            const data = {
                                loci: [
                                    StructureElement.Loci(bond.aStructure, [{unit: bond.aUnit, indices: OrderedSet.ofSingleton(bond.aIndex)}]),
                                    StructureElement.Loci(bond.bStructure, [{unit: bond.bUnit, indices: OrderedSet.ofSingleton(bond.bIndex)}])
                                ]
                            };
                            const label = bundleLabel(data, {htmlStyling: false});
                            const splittedLabel = label.split("|");
                            const splittedRes = splittedLabel[4].trim().split(" ");
                            const auth_asym_id = splittedLabel[3].trim();
                            const auth_seq_id = splittedRes[1].trim();
                            const comp_id = splittedRes[0].trim();
                            setSortOrderState({res: ""})
                            sortOnClick(auth_seq_id, auth_asym_id, comp_id);
                        }

                        if (e.current.loci.kind === "element-loci") {
                            let loci = e.current.loci;
                            let se = StructureElement.Stats.ofLoci(loci)
                            let location = se.firstElementLoc;
                            /*const rI = StructureElement.Location.residueIndex(location);
                            const label_asym_id = Props.chain.label_asym_id(location);
                            const auth_asym_id = Props.chain.auth_asym_id(location);
                            const has_label_seq_id = location.unit.model.atomicHierarchy.residues.label_seq_id.valueKind(rI) === 0
                            const label_seq_id = Props.residue.label_seq_id(location);
                            const auth_seq_id = Props.residue.auth_seq_id(location);
                            const ins_code = Props.residue.pdbx_PDB_ins_code(location);
                            const comp_id = Props.atom.label_comp_id(location);
                            const atom_id = Props.atom.label_atom_id(location);
                            const alt_id = Props.atom.label_alt_id(location);
                            const occupancy = Props.atom.occupancy(location);
                            const microHetCompIds = Props.residue.microheterogeneityCompIds(location);*/
                            const auth_seq_id = Props.residue.auth_seq_id(location);
                            const auth_asym_id = Props.chain.auth_asym_id(location);
                            const comp_id = Props.atom.label_comp_id(location);
                            setSortOrderState({col: ""})
                            sortOnClick(auth_seq_id, auth_asym_id, comp_id);
                        }
                    });

            } catch (e) {
                console.log(e);
            }
        }

        init();

        return () => {
            //window.molstar?.dispose();
            //window.molstar = undefined;
        };

    }, []);

    const selectedInteraction = (label) => {
        console.log(label);
        const splittedLabel = label.split("%");
        const interaction = {
            chain1: '', residue1: parseInt(splittedLabel[1]), atom1: splittedLabel[2],
            chain2: '', residue2: parseInt(splittedLabel[3]), atom2: splittedLabel[4]
        };
        setClickedInteraction(interaction);
    };

    const selectAllRes = async (MAIN_TABLE_DATA) => {
        const Q = MolScriptBuilder;
        const components = window.molstar.managers.structure.hierarchy.current.structures[0].components;
        const cell = window.molstar.managers.structure.hierarchy.current.structures[0].cell;
        const objdata = window.molstar.managers.structure.hierarchy.current.structures[0].cell.obj.data;

        const groups = {};
        const colors = [{
            'SideChain': Color.fromRgb(0, 0, 139),
            'Chain': Color.fromRgb(30, 144, 255)
        }, {
            'SideChain': Color.fromRgb(255, 105, 180),
            'Chain': Color.fromRgb(255, 182, 193)
        }];

        for (const row of MAIN_TABLE_DATA) {
            for (const chainDes of [[row[2], row[3]], [row[5], row[6]]]) {
                const chain = {"name": chainDes[1], "residue": parseInt(chainDes[0])};

                const atomGroups = Q.struct.generator.atomGroups({
                    'chain-test': Q.core.rel.eq([Q.struct.atomProperty.macromolecular.auth_asym_id(), chain.name]),
                    'residue-test': Q.core.rel.eq([Q.struct.atomProperty.macromolecular.auth_seq_id(), chain.residue]),
                });

                groups[chain.name] = groups[chain.name] || [];
                groups[chain.name].push(atomGroups);

            }
        }

        /*const atomGroupsChain = Q.struct.generator.atomGroups({ 'chain-test': Q.core.rel.inRange([Q.struct.atomProperty.macromolecular.auth_asym_id(), 'A', 'B']) });
        let selectionChain = Script.getStructureSelection((Q) => atomGroupsChain, objdata);
        let getLociChain = async (s) => StructureSelection.toLociWithSourceUnits(selectionChain);
        await setStructureOverpaint(window.molstar, components, colors['AChain'], getLociChain);*/

        let index = 0;
        for (const chain in groups) {
            // CHAIN
            const atomGroupsChain = Q.struct.generator.atomGroups({
                'chain-test': Q.core.rel.eq([Q.struct.atomProperty.macromolecular.auth_asym_id(), chain]),
                'entity-test': Q.core.rel.neq([MS.ammp('entityType'), 'water'])
            });

            let selectionChain = Script.getStructureSelection((Q) => atomGroupsChain, objdata);
            let getLociChain = async (s) => StructureSelection.toLociWithSourceUnits(selectionChain);
            await setStructureOverpaint(window.molstar, components, colors[index]['Chain'], getLociChain);

            // RESIUDE IN INTERACTION
            let selection = Script.getStructureSelection((Q) => Q.struct.combinator.merge(groups[chain]), objdata);
            let getLoci = async (s) => StructureSelection.toLociWithSourceUnits(selection);
            await setStructureOverpaint(window.molstar, components, colors[index]['SideChain'], getLoci);

            // BALL AND STICK
            let component = await window.molstar.builders.structure.tryCreateComponentFromExpression(cell, selection, 'selection-ball-and-stick-' + chain);
            //window.molstar.builders.structure.representation.addRepresentation(component, { type: 'ball-and-stick', color: 'element-symbol' });
            //window.molstar.builders.structure.representation.addRepresentation(component, { type: 'ball-and-stick', color: 'uniform' }); // TUTTI LO STESSO COLORE
            await window.molstar.builders.structure.representation.addRepresentation(component, {type: 'ball-and-stick', color: 'element-symbol', colorParams: {carbonColor: {name: 'uniform', params: {value: colors[index]['SideChain']}}}}); //
            index++;
        }
    }

    const tagAllInteraction = async (interactionType, interactions, toggleInteractionStatus) => {

        const Q = MolScriptBuilder;
        const objdata = window.molstar.managers.structure.hierarchy.current.structures[0].cell.obj.data;
        const cell = window.molstar.managers.structure.hierarchy.current.structures[0].cell;

        /*const atomGroups = Q.struct.generator.atomGroups({
            'chain-test': Q.core.rel.eq([Q.struct.atomProperty.macromolecular.auth_asym_id(), 'A']),
            'residue-test': Q.core.rel.eq([Q.struct.atomProperty.macromolecular.auth_seq_id(), 27]),
        });*/

        const chains = interactions.flatMap(i => [i[3], i[6]]);
        const residues = interactions.flatMap(i => [parseInt(i[2]), parseInt(i[5])]);

        const atomGroups = Q.struct.generator.atomGroups({
            'chain-test': Q.core.set.has([Q.set(...chains), Q.ammp('auth_asym_id')]),
            'residue-test': Q.core.set.has([Q.set(...residues), Q.ammp('auth_seq_id')])
        });

        let selection = Script.getStructureSelection(atomGroups, objdata);
        let component = await window.molstar.builders.structure.tryCreateComponentFromExpression(cell, selection, 'label');
        if (toggleInteractionStatus) {
            await window.molstar.builders.structure.representation.addRepresentation(component, {type: 'label', typeParams: {level: 'residue',}, color: 'uniform'});
        } else {
            // C:\sviluppo\misc\chem\coco2\node_modules\molstar\lib\mol-plugin-ui\structure\components.js
            await window.molstar.managers.structure.component.removeRepresentations([{representations: [component]}]);
        }
    };

    const allInteraction = async (interactionType, interactions, toggleInteractionStatus) => {
        if (toggleInteractionStatus) {
            const objdata = window.molstar.managers.structure.hierarchy.current.structures[0].cell.obj.data;
            const Q = MolScriptBuilder;

            const atomGroups = Q.struct.generator.atomGroups({
                'chain-test': Q.core.rel.eq([Q.struct.atomProperty.macromolecular.auth_asym_id(), interactions[0].chain1]),
                'residue-test': Q.core.rel.eq([Q.struct.atomProperty.macromolecular.auth_seq_id(), interactions[0].residue1])
            });

            const selection = Script.getStructureSelection((Q) => atomGroups, objdata);
            let loci = StructureSelection.toLociWithSourceUnits(selection);
            window.molstar.managers.camera.focusLoci(loci);
        }

        for (const interaction of interactions) {
            await onNestedRowClick(interaction, interactionType);
        }
    }

    const onRowClick = (r) => {
        return {
            onClick: (e) => {
                zoom_in([[r[3], r[2]], [r[6], r[5]]]);
            }
        }
    };

    const build_tag = (coord_pair, interactiontype) => {
        return coord_pair.flat().join('_') + "_" + interactiontype.replaceAll(" ", "_");
    }

    const onNestedRowClick = async (row, interactiontype, zoomin = true) => {
        const state = window.molstar.state.data;

        let distances = [];
        distances[0] = [[row[3], row[2], row[7]], [row[6], row[5], row[8]]];

        if (interactiontype === "Water mediated contact" || interactiontype === "Metal mediated contact") {
            const mediator = row[11].split("_");
            const atom = interactiontype === "Water mediated contact" ? "O" : mediator[2];
            distances[0] = [[row[3], row[2], row[7]], [mediator[0], parseInt(mediator[1]), atom]];
            distances[1] = [[row[6], row[5], row[8]], [mediator[0], parseInt(mediator[1]), atom]];
        }

        if (interactiontype === "Cation-π interaction") {
            const ring = row[10].split('-')[0];
            distances[0] = [[row[6], row[5], RING_ATOMS[ring]], [row[3], row[2], row[8]]];
        }

        if (interactiontype === "π-π interaction") {
            distances[0] = [[row[3], row[2], RING_ATOMS[row[1]]], [row[6], row[5], RING_ATOMS[row[4]]]];
        }

        if (interactiontype === "lp-π interaction" || interactiontype === "Amino-π interaction" || interactiontype === "Anion-π interaction") {
            const ring = row[10].split('-')[0];
            if (ring === row[1]) {
                distances[0] = [[row[3], row[2], RING_ATOMS[ring]], [row[6], row[5], row[8]]];
            } else {
                distances[0] = [[row[6], row[5], RING_ATOMS[ring]], [row[3], row[2], row[8]]];
            }
        }

        if (interactiontype === "CH-π interaction") {
            const ring = row[11].split('-')[0];
            if (ring === row[1]) {
                distances[0] = [[row[3], row[2], RING_ATOMS[ring]], [row[6], row[5], row[8]]];
            } else {
                distances[0] = [[row[6], row[5], RING_ATOMS[ring]], [row[3], row[2], row[8]]];
            }
        }

        if (interactiontype === "Cation-π interaction") {
            const ring = row[10].split('-')[0];
            distances[0] = [[row[3], row[2], RING_ATOMS[ring]], [row[6], row[5], row[8]]];
        }

        for (const distance of distances) {
            const tag = build_tag(distance, interactiontype);
            let groupRef = StateSelection.findTagInSubtree(state.tree, StateTransform.RootRef, tag);

            if (groupRef) {
                await window.molstar.state.data.build().delete(groupRef).commit();
            } else {
                await selectInMol(distance, INTERACTION_COLOR[interactiontype], interactiontype, true, zoomin, tag);
            }
        }
    };

    const isExpandable = (record) => {
        return true;
    };

    const expandedRowRender = (record, index, indent, expanded) => {

        const interactionsType = record[7].split(';').reduce((acc, item) => {
            item = item.trim();
            if (item) {
                acc[item] = (acc[item] || 0) + 1;
            }
            return acc;
        }, {});

        const interactionsTypeEntries = Object.keys(interactionsType);

        return (
            <>
                {interactionsTypeEntries.map((it, idx) => (
                    <Row justify="center">
                        <Col span={20} style={{marginBottom: '25px'}}>
                            <InteractionInnerTable interactionType={it} record={record} onNestedRowClick={onNestedRowClick} allInteraction={allInteraction} tagAllInteraction={tagAllInteraction} selectedInteraction={clickedInteraction}
                                                   pdb={results.pdb}
                                                   folder={results.dataFolder}></InteractionInnerTable>
                        </Col>
                    </Row>
                ))}
            </>
        )
    };

    const rowClassName = (record) => {
        return record[8] ? 'highlight' : '';
    }

    return (
        <>
            <div style={{height: '100%'}} ref={container}>
                <Row id="molstar">

                    <Col span={12}>
                        <div style={{paddingLeft: '20px', paddingRight: '20px'}} className={"main-table-container"}>
                            <Divider orientation="center" style={{fontSize: '18px'}}>
                                Table of Atomic Interactions (click on + for details)
                                <a href="/BioTools/cocomaps2/help" target="_blank" rel="noopener noreferrer">
                                    <InfoCircleOutlined style={{color: '#1890ff', cursor: 'pointer', paddingLeft: '10px', fontSize: '16px'}}/>
                                </a>
                            </Divider>
                            <div style={{textAlign: 'center'}}>
                                <Text type="secondary" style={{fontSize: 12, display: 'block', marginTop: -12}}>
                                    * The interatomic distance for this interaction is below the sum of the respective van der Waals radii.
                                </Text>
                            </div>
                            <Table rowKey={0} dataSource={sortedData} columns={mainTableColumns} size={"small"} onRow={onRowClick} expandable={{expandedRowRender}} rowExpandable={isExpandable} rowClassName={rowClassName}
                                   pagination={false} scroll={{y: firstRowDivHeight - 100}}
                                   onChange={(pagination, filters, sorter) => {
                                       setSortOrderState(() => {
                                           return {col: sorter.column ? sorter.column.title : null, order: sorter.order}
                                       });
                                   }
                                   }
                            />
                        </div>
                    </Col>

                    <Col span={12}>
                        <div style={{position: 'relative', overflow: 'hidden', zIndex: 1000}}>
                            <div ref={parent} style={{height: firstRowDivHeight}}/>
                        </div>
                    </Col>

                </Row>

                <Row id="charts" style={{height: '100%', marginTop: '40px'}}>
                    <Col span={24}>
                        {mainData && (
                            <Multichart mainData={mainData} results={results} firstRowDivHeight={firstRowDivHeight - 100} pdb={results.pdb} onNestedRowClick={onNestedRowClick} folder={results.dataFolder} sortOnClick={sortSingleRowOnClick}
                                        sortInteractionTypeOnClick={sortInteractionTypeRowOnClick}></Multichart>
                        )}
                    </Col>
                </Row>

                {mainData && (
                    <Row id="ASA" style={{height: '100%'}}>
                        <Col span={24} style={{paddingLeft: '20px', paddingRight: '20px'}}>
                            <div style={{zIndex: 1}}>
                                <Tabs defaultActiveKey="1" centered>
                                    <Tabs.TabPane tab={
                                        <span>
                                             ASA Table
                                              <a href="/BioTools/cocomaps2/help" target="_blank" rel="noopener noreferrer">
                                                <InfoCircleOutlined style={{color: '#1890ff', paddingLeft: '10px'}}/>
                                              </a>
                                          </span>
                                    } key="1">
                                        <CompoundTable tableName={"complex.pdb_Rsa_stats"} record={mainData[0]} isProperty={true} pdb={results.pdb} folder={results.dataFolder}></CompoundTable>
                                    </Tabs.TabPane>

                                    <Tabs.TabPane
                                        tab={
                                            <span>
                                             {"ASA Table for " + (results.mol1 ? results.mol1 : " chain 1")}
                                                <a href="/BioTools/cocomaps2/help" target="_blank" rel="noopener noreferrer">
                                                <InfoCircleOutlined style={{color: '#1890ff', paddingLeft: '10px'}}/>
                                              </a>
                                          </span>
                                        }
                                        key="2">
                                        <CompoundTable tableName={"complex.pdb_ASA_table_chain1"} record={mainData[0]} isProperty={false} pdb={results.pdb} folder={results.dataFolder}></CompoundTable>
                                    </Tabs.TabPane>

                                    <Tabs.TabPane
                                        tab={
                                            < span>
                                            {"ASA Table for " + (results.mol2 ? results.mol2 : " chain 2")}
                                                < a href="/BioTools/cocomaps2/help" target="_blank" rel="noopener noreferrer">
                                                <InfoCircleOutlined style={{color: '#1890ff', paddingLeft: '10px'}}/>
                                            </a>
                                            </span>
                                        }
                                        key="3">
                                        <CompoundTable
                                            tableName={"complex.pdb_ASA_table_chain2"} record={mainData[0]} isProperty={false} pdb={results.pdb} folder={results.dataFolder}></CompoundTable>
                                    </Tabs.TabPane>
                                </Tabs>
                            </div>
                        </Col>
                    </Row>
                )}
            </div>
            {results &&
                (<Modal title="Results" centered open={noResultModal} footer={null} closable={false}>
                    <p>No interaction found for {results.chain1} vs {results.chain2}</p>
                    <a href={"/BioTools/cocomaps2"}>Return to home page</a>
                </Modal>)}
        </>
    )

}